import { useMemo, useState } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Space, Tag, Typography,
  Popconfirm, Switch, Tabs, Alert, Row, Col, App, Tooltip,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined, AppstoreOutlined,
  ApartmentOutlined, BookOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import {
  useCatalogTypes, useCreateCatalogType, useUpdateCatalogType, useDeleteCatalogType,
} from '@hooks/useMetadata';
import type {
  MetaDocumentTypeDto, CreateMetaCatalogTypeRequest, UpdateMetaCatalogTypeRequest,
} from '@/types/metadata';
import AttributesEditor from './AttributesEditor';

const { Title, Text } = Typography;

interface CatalogFormValues {
  code: string;
  name: string;
  icon?: string;
  navGroup?: string;
  isActive?: boolean;
  isHierarchical?: boolean;
  ownerType?: string;
}

interface CatalogTypesPageProps {
  /** Колбек "Відкрити записи довідника" — викликається при кліку на кнопку «Записи».
   *  Передає typeCode; родитель вирішує куди роутити (ConfigurationPage перемикає на DynamicCatalogItemsPage). */
  acqOnOpenItems?: (acqTypeCode: string) => void;
}

/**
 * Конструктор довідників — паралель до DocumentTypesPage, але для Kind="catalog".
 * Спецполя довідників: IsHierarchical, OwnerType (для підпорядкованих довідників типу
 * "ОдиниціВиміруНоменклатури" -> "Номенклатура").
 *
 * Реквізити (MetaAttribute) спільні з документами — переюзаємо AttributesEditor.
 * Layout / Event Handlers поки не реалізовані для каталогів (відсутні endpoints
 * /catalog-types/{id}/layout та /event-handlers); можна додати пізніше за потреби.
 */
export default function CatalogTypesPage({ acqOnOpenItems }: CatalogTypesPageProps = {}) {
  const acqTypesQuery = useCatalogTypes();
  const acqCreate = useCreateCatalogType();
  const acqUpdate = useUpdateCatalogType();
  const acqDelete = useDeleteCatalogType();
  const { message: acqMsg } = App.useApp();

  const [acqOpen, acqSetOpen] = useState(false);
  const [acqEditing, acqSetEditing] = useState<MetaDocumentTypeDto | null>(null);
  const [acqForm] = Form.useForm<CatalogFormValues>();
  const [acqSearch, acqSetSearch] = useState('');

  const acqTypes = acqTypesQuery.data ?? [];

  const acqFiltered = useMemo(() => {
    const q = acqSearch.trim().toLowerCase();
    return q
      ? acqTypes.filter((t) => t.code.toLowerCase().includes(q) || t.name.toLowerCase().includes(q))
      : acqTypes;
  }, [acqTypes, acqSearch]);

  // Для OwnerType select — список усіх існуючих каталогів (окрім самого редагованого).
  const acqOwnerOptions = useMemo(() =>
    acqTypes
      .filter((t) => !acqEditing || t.id !== acqEditing.id)
      .map((t) => ({ value: t.code, label: `${t.code} — ${t.name}` })),
  [acqTypes, acqEditing]);

  const acqHandleAdd = () => {
    acqSetEditing(null);
    acqForm.resetFields();
    acqForm.setFieldsValue({ isActive: true, isHierarchical: false });
    acqSetOpen(true);
  };

  const acqHandleEdit = (acqRow: MetaDocumentTypeDto) => {
    acqSetEditing(acqRow);
    acqForm.setFieldsValue({
      code: acqRow.code,
      name: acqRow.name,
      icon: acqRow.icon ?? undefined,
      navGroup: acqRow.navGroup ?? undefined,
      isActive: acqRow.isActive,
      isHierarchical: acqRow.isHierarchical ?? false,
      ownerType: acqRow.ownerType ?? undefined,
    });
    acqSetOpen(true);
  };

  const acqHandleSubmit = async () => {
    const v = await acqForm.validateFields();
    if (acqEditing) {
      const acqReq: UpdateMetaCatalogTypeRequest = {
        name: v.name,
        icon: v.icon ?? null,
        navGroup: v.navGroup ?? null,
        isActive: v.isActive ?? true,
        isHierarchical: v.isHierarchical ?? false,
        ownerType: v.ownerType ?? null,
      };
      await acqUpdate.mutateAsync({ acqId: acqEditing.id, acqReq });
    } else {
      const acqReq: CreateMetaCatalogTypeRequest = {
        code: v.code,
        name: v.name,
        icon: v.icon ?? null,
        navGroup: v.navGroup ?? null,
        isHierarchical: v.isHierarchical ?? false,
        ownerType: v.ownerType ?? null,
        attributes: [],
      };
      await acqCreate.mutateAsync(acqReq);
    }
    acqSetOpen(false);
    acqForm.resetFields();
  };

  const acqHandleDelete = async (acqRow: MetaDocumentTypeDto) => {
    try {
      await acqDelete.mutateAsync(acqRow.id);
    } catch {
      acqMsg.error('Не вдалося видалити');
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <Space size={8}>
            <Title level={4} style={{ margin: 0 }}>
              <BookOutlined /> Конструктор довідників
            </Title>
            <Text type="secondary">{acqTypes.length} тип{acqTypes.length === 1 ? '' : 'ів'}</Text>
          </Space>
          <Space>
            <Input
              placeholder="Пошук за кодом / назвою…"
              prefix={<SearchOutlined />}
              value={acqSearch}
              onChange={(e) => acqSetSearch(e.target.value)}
              style={{ width: 240 }}
              allowClear
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={acqHandleAdd}>
              Новий довідник
            </Button>
          </Space>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Створюй кастомні довідники без написання коду. Реквізити зберігаються у спільному JSONB-сховищі
          з документами (DynamicDocument). Для підпорядкованих довідників — заповни «Тип-власник».
        </Text>
      </Card>

      <Table
        rowKey="id"
        size="small"
        loading={acqTypesQuery.isLoading}
        dataSource={acqFiltered}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100] }}
        columns={[
          {
            title: 'Код',
            dataIndex: 'code',
            width: 200,
            render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
          },
          { title: 'Назва', dataIndex: 'name' },
          {
            title: 'Група',
            dataIndex: 'navGroup',
            width: 130,
            render: (v?: string) => v ? <Tag>{v}</Tag> : null,
          },
          {
            title: 'Ієрарх.',
            dataIndex: 'isHierarchical',
            width: 80,
            render: (v?: boolean) => v ? <Tag color="purple" icon={<ApartmentOutlined />}>так</Tag> : null,
          },
          {
            title: 'Власник',
            dataIndex: 'ownerType',
            width: 160,
            render: (v?: string | null) => v ? <Tag>{v}</Tag> : null,
          },
          {
            title: 'Стан',
            dataIndex: 'isActive',
            width: 100,
            render: (v: boolean) => (v ? <Tag color="green">Активний</Tag> : <Tag>Неактивний</Tag>),
          },
          { title: 'v', dataIndex: 'version', width: 50 },
          {
            title: 'Дії',
            key: 'actions',
            width: 220,
            render: (_, acqRow) => (
              <Space size={2}>
                {acqOnOpenItems && (
                  <Tooltip title="Записи довідника">
                    <Button
                      type="link"
                      size="small"
                      icon={<UnorderedListOutlined />}
                      onClick={() => acqOnOpenItems(acqRow.code)}
                    >
                      Записи
                    </Button>
                  </Tooltip>
                )}
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => acqHandleEdit(acqRow)}>
                  Редагувати
                </Button>
                <Popconfirm
                  title="Видалити тип довідника?"
                  description="Дія soft-delete; записи довідника лишаться у БД."
                  okText="Так"
                  cancelText="Ні"
                  onConfirm={() => acqHandleDelete(acqRow)}
                >
                  <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      {/* Edit / Create modal */}
      <Modal
        title={acqEditing ? `Редагувати: ${acqEditing.name}` : 'Новий довідник'}
        open={acqOpen}
        onCancel={() => acqSetOpen(false)}
        onOk={acqHandleSubmit}
        confirmLoading={acqCreate.isPending || acqUpdate.isPending}
        okText="Зберегти"
        cancelText="Скасувати"
        width={760}
        destroyOnHidden
      >
        <Tabs
          defaultActiveKey="general"
          items={[
            {
              key: 'general',
              label: <span><AppstoreOutlined /> Загальне</span>,
              children: (
                <Form form={acqForm} layout="vertical">
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        name="code"
                        label="Код (BAS-style)"
                        rules={[{ required: true, message: 'Обовʼязкове' }, { pattern: /^[a-z][a-z0-9_]*$/, message: 'lower_snake_case' }]}
                        extra="Унікальний ідентифікатор. Не змінюється після створення."
                      >
                        <Input placeholder="наприклад: contractor_group" disabled={!!acqEditing} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="name" label="Назва (UI)" rules={[{ required: true, message: 'Обовʼязкове' }]}>
                        <Input placeholder="наприклад: Група контрагентів" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="icon" label="Іконка (AntD-key)">
                        <Input placeholder="наприклад: BookOutlined" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="navGroup" label="Група меню">
                        <Input placeholder="наприклад: Бізнес" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Спецполя довідника"
                    description={<>
                      <b>Ієрархічний</b> — довідник підтримує батьківський елемент (групи). <b>Тип-власник</b> — підпорядковує довідник іншому
                      (записи цього довідника мають Owner → запис власника), наприклад «Одиниці виміру номенклатури» підпорядковані «Номенклатурі».
                    </>}
                  />
                  <Row gutter={12}>
                    <Col span={8}>
                      <Form.Item name="isHierarchical" label="Ієрархічний" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item name="ownerType" label="Тип-власник (підпорядкування)" extra="Залиш порожнім, якщо незалежний.">
                        <Select
                          allowClear
                          placeholder="— незалежний —"
                          showSearch
                          optionFilterProp="label"
                          options={acqOwnerOptions}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {acqEditing && (
                    <Form.Item name="isActive" label="Активний" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  )}
                </Form>
              ),
            },
            {
              key: 'attributes',
              label: <span>Реквізити</span>,
              disabled: !acqEditing,
              children: acqEditing
                ? <AttributesEditor acqType={acqEditing} />
                : <Alert type="info" message="Збережи довідник, потім зможеш додати реквізити." />,
            },
          ]}
        />
      </Modal>
    </>
  );
}
